import {
  LAYOUT_VERSION,
  type HomeLayout,
  type TileGroup,
  type SidebarSection,
} from './home-schema.js';

/**
 * The Security folder (home-base group) and sidebar section. Exported so the
 * layout migration can inject them into existing per-user layouts that predate
 * them. Admins see all groups; non-admins only see groups in their allowed set,
 * so this stays admin-only by simply not being in any non-admin allow-list.
 */
export const SECURITY_GROUP: TileGroup = {
  id: 'security-group',
  title: 'Security',
  tiles: [
    {
      id: 'security-actions',
      label: 'Security Actions',
      color: 'terracotta',
      glyph: '⚿',
      href: '/security',
    },
  ],
};

export const SECURITY_SIDEBAR_SECTION: SidebarSection = {
  id: 'security',
  title: 'Security',
  items: [
    { id: 'security-actions-sb', label: 'Security Actions', href: '/security', icon: '⚿', roles: ['admin'] },
  ],
};

/**
 * Entity Documents — a dynamic group that renders one tile per active entity
 * (from the entities collection). Sits directly below the Documents group.
 * Exported so the migration can inject it into pre-existing layouts.
 */
export const ENTITY_DOCUMENTS_GROUP: TileGroup = {
  id: 'entity-documents-group',
  title: 'Entity Documents',
  dynamic: 'entities',
  tiles: [],
};

/**
 * Default SFP home layout — applied to any user with no saved layout.
 * Users customize freely from this starting point.
 *
 * Design notes:
 *  - sidebar: deep ink with subtle SFP mark
 *  - main: warm parchment background
 *  - tile palette: family-office muted (forest, navy, plum, gold, sage…)
 */
export const DEFAULT_HOME_LAYOUT: HomeLayout = {
  version: LAYOUT_VERSION,
  brand: {
    name: 'SFP',
    tagline: 'Shulkin Family Partners',
    markGlyph: '◆',
  },
  sidebar: [
    {
      id: 'primary',
      items: [
        { id: 'home', label: 'Home base', href: '/', icon: '◉' },
        { id: 'documents', label: 'Documents', href: '/documents', icon: '▤' },
        { id: 'library', label: 'Library', href: '/documents/library', icon: '⛁' },
        { id: 'processing', label: 'Processing', href: '/documents/processing', icon: '◔', roles: ['admin'] },
        { id: 'bills', label: 'Bills', href: '/bills', icon: '◐' },
        { id: 'accounts', label: 'Accounts', href: '/accounts', icon: '◇' },
        { id: 'entities', label: 'Entities', href: '/entities', icon: '⬡' },
        { id: 'org', label: 'Org Chart', href: '/org', icon: '⋔' },
        { id: 'financials', label: 'Financials', href: '/financials', icon: '$' },
        { id: 'assets', label: 'Assets', href: '/assets', icon: '◈' },
        { id: 'trading', label: 'Trading', href: '/trading', icon: '↗' },
      ],
    },
    {
      id: 'workspace',
      items: [
        { id: 'workspace', label: 'My workspace', href: '/workspace', icon: '◌' },
      ],
    },
    {
      id: 'integrations',
      title: 'Integrations',
      items: [
        { id: 'dropbox-sb', label: 'Dropbox', href: '/integrations/dropbox', icon: '⬚' },
        { id: 'storage-sb', label: 'Storage', href: '/integrations/storage', icon: '◰' },
        { id: 'files-sb', label: 'File Explorer', href: '/files', icon: '▤' },
      ],
    },
    {
      id: 'admin',
      title: 'Admin',
      items: [
        { id: 'invite', label: 'Invite user', href: '/admin/invite', icon: '@', roles: ['admin'] },
        { id: 'users', label: 'Users', href: '/admin/users', icon: '☖', roles: ['admin'] },
        { id: 'family', label: 'Family members', href: '/admin/family', icon: '♛', roles: ['admin'] },
        { id: 'audit', label: 'Audit log', href: '/admin/audit', icon: '⌗', roles: ['admin'] },
        { id: 'security', label: 'Security Actions', href: '/security', icon: '⚿', roles: ['admin'] },
        { id: 'settings', label: 'Settings', href: '/settings', icon: '⚙' },
      ],
    },
  ],
  topActions: [
    { id: 'edit', label: 'Edit this page', kind: 'edit' },
    { id: 'ask', label: 'Ask SFP', kind: 'ask' },
    { id: 'notes', label: 'Notes', kind: 'notes' },
  ],
  groups: [
    {
      id: 'documents-group',
      title: 'Documents',
      tiles: [
        { id: 'all-docs', label: 'All Documents', color: 'navy', glyph: '▤', href: '/documents' },
        { id: 'library', label: 'Library (by entity)', color: 'forest', glyph: '⛁', href: '/documents/library' },
        {
          id: 'triage',
          label: 'Triage Queue',
          color: 'amber',
          glyph: '!',
          href: '/documents/triage',
          badge: { kind: 'count', source: 'triage' },
        },
        { id: 'processing', label: 'Processing Monitor', color: 'plum', glyph: '◔', href: '/documents/processing' },
        { id: 'reclassify', label: 'Reclassify', color: 'terracotta', glyph: '⇄', href: '/documents/reclassify' },
        { id: 'upload', label: 'Upload', color: 'sage', glyph: '⤴', href: '/documents?upload=1' },
        { id: 'export', label: 'Master Export', color: 'slate', glyph: '⬇', href: '/documents/export' },
      ],
    },
    // Entity Documents — auto-populated (one tile per entity), sits below Documents.
    ENTITY_DOCUMENTS_GROUP,
    {
      id: 'bills-group',
      title: 'Bill Pay',
      tiles: [
        {
          id: 'bills',
          label: 'Bills Inbox',
          color: 'forest',
          glyph: '◐',
          href: '/bills',
          badge: { kind: 'count', source: 'bills_due_week' },
        },
        {
          id: 'past-due',
          label: 'Past Due',
          color: 'terracotta',
          glyph: '⚠',
          href: '/bills?status=past_due',
          badge: { kind: 'count', source: 'bills_past_due' },
        },
        { id: 'calendar', label: 'Calendar', color: 'plum', glyph: '◫', href: '/bills/calendar' },
        { id: 'accounts', label: 'Accounts', color: 'mist', glyph: '◇', href: '/accounts' },
      ],
    },
    {
      id: 'entities-group',
      title: 'Entities & Assets',
      tiles: [
        { id: 'entities', label: 'Entities', color: 'navy', glyph: '⬡', href: '/entities' },
        { id: 'org-chart', label: 'Org Chart', color: 'plum', glyph: '⋔', href: '/org' },
        { id: 'financials', label: 'Financials', color: 'forest', glyph: '$', href: '/financials' },
        { id: 'asset-roll', label: 'Asset Rollup', color: 'gold', glyph: '◈', href: '/assets' },
        { id: 'liabilities', label: 'Liabilities', color: 'terracotta', glyph: '⊖', href: '/assets/liabilities' },
        { id: 'valuation', label: 'Valuations', color: 'mist', glyph: '◎', href: '/assets/valuations' },
      ],
    },
    {
      id: 'trading-group',
      title: 'Trading',
      tiles: [
        { id: 'trading-desk', label: 'Trading Desk', color: 'forest', glyph: '↗', href: '/trading' },
      ],
    },
    {
      id: 'family-group',
      title: 'Family',
      tiles: [
        { id: 'estate', label: 'Estate Plan', color: 'plum', glyph: '✦', href: '/documents?category=legal_personal' },
        { id: 'tax', label: 'Tax Returns', color: 'amber', glyph: '§', href: '/documents?category=tax' },
        { id: 'insurance', label: 'Insurance', color: 'mist', glyph: '◍', href: '/documents?category=insurance' },
        { id: 'governance', label: 'Governance', color: 'slate', glyph: '☖', href: '/documents?category=governance' },
      ],
    },
    {
      id: 'sources-group',
      title: 'Sources',
      tiles: [
        { id: 'gmail', label: 'Bills Inbox', color: 'rose', glyph: '✉', href: 'https://mail.google.com', external: true },
        { id: 'plaid', label: 'Plaid', color: 'sage', glyph: 'P', href: 'https://dashboard.plaid.com', external: true },
        { id: 'dropbox-src', label: 'Dropbox', color: 'navy', glyph: '⬚', href: '/integrations/dropbox' },
      ],
    },
    {
      id: 'tools-group',
      title: 'Tools',
      tiles: [
        { id: 'search', label: 'Search', color: 'ink', glyph: '⌕', href: '/search' },
        { id: 'files', label: 'File Explorer', color: 'forest', glyph: '▤', href: '/files' },
        { id: 'reports', label: 'Reports', color: 'slate', glyph: '◰', href: '/reports' },
        { id: 'workspace', label: 'My Workspace', color: 'parchment', glyph: '◌', href: '/workspace' },
        { id: 'storage-info', label: 'Where is my data?', color: 'mist', glyph: '◰', href: '/integrations/storage' },
        { id: 'mfa-settings', label: 'Two-factor', color: 'amber', glyph: '⚿', href: '/settings/mfa' },
      ],
    },
    {
      id: 'integrations-group',
      title: 'Integrations',
      tiles: [
        { id: 'dropbox-int', label: 'Dropbox', color: 'navy', glyph: '⬚', href: '/integrations/dropbox' },
        { id: 'gmail-int', label: 'Gmail (bills)', color: 'rose', glyph: '✉', href: 'https://mail.google.com', external: true },
        { id: 'plaid-int', label: 'Plaid', color: 'sage', glyph: 'P', href: 'https://dashboard.plaid.com', external: true },
      ],
    },
    {
      id: 'admin-group',
      title: 'Admin',
      tiles: [
        { id: 'setup-email', label: 'Setup Email', color: 'gold', glyph: '@', href: '/admin/invite' },
        { id: 'users-admin', label: 'Users', color: 'navy', glyph: '☖', href: '/admin/users' },
        { id: 'family-admin', label: 'Family', color: 'rose', glyph: '♛', href: '/admin/family' },
        { id: 'audit-admin', label: 'Audit Log', color: 'slate', glyph: '⌗', href: '/admin/audit' },
      ],
    },
    // Dedicated Security folder — its own window on the home base (admin-only).
    SECURITY_GROUP,
  ],
  rightColumn: [
    {
      id: 'processing-status',
      kind: 'processing_status',
      title: 'Document processing',
    },
    {
      id: 'action-required',
      kind: 'action_required',
      title: 'Action required',
      primaryLabel: 'Triage',
      secondaryLabel: 'Past due bills',
      inboxHref: '/documents/triage',
    },
    {
      id: 'queue',
      kind: 'queue',
      title: 'My queue',
      source: 'triage',
      limit: 3,
    },
    {
      id: 'recent',
      kind: 'recent_documents',
      title: 'Recently added',
      limit: 5,
    },
    {
      id: 'workspace-note',
      kind: 'note',
      title: 'My workspace',
      body: 'Click "Edit this page" to add widgets, custom tiles, or scratch notes here.',
    },
  ],
};
